/**
 * FinanceLens.jsx - Tracking > Summary, the finance view: the MASTER SUMMARY
 * of the tracking master Rev 1.7.4, in plain words.
 *   - headline: As sold, As sold cost, gross profit, margin (cost area only),
 *     the budget / Quoted figure; without the cost area: As sold, budget,
 *     invoiced, not invoiced yet - and a line that cost and margin are
 *     for the 'cost-rates' area (never a $0 in their place)
 *   - money in: PO value, invoiced, paid, open receivables, PO remaining,
 *     As sold not invoiced yet (the invoice ledger; INVOICING SUMMARY L-P)
 *   - PO lines (value, invoiced, remaining, Over / Under Budget, work booked)
 *   - As sold built up (labour, expenses + fee, equipment & materials,
 *     non-billable receipts) against the budget
 *   - T&L / Fixed (S2 / S3), expenses billable / non-billable with the fee
 *   - project margin analysis (B11-G17), summary by task (rows 20-32) and by
 *     engineer (rows 37-60)
 * Every figure is the engine's (data.tracking); nothing is priced again here.
 */
import { GREEN, MUTED, Section, td } from '../quotes/quoteUi';
import { hours, money, pct } from '../costs/costApi';
import { budgetSoldOf } from './trackingInsights';
import { Bar, EXPENSE_COLS, EXPENSE_LABELS, Empty, Head, Hint, Num, Stat, feeText } from './trackingUi';

const tone = (m) => (m >= 0.3 ? GREEN : m >= 0.15 ? '#B07503' : '#B71C1C');
const label = () => ({ ...td, fontSize: 12, fontWeight: 600 });
const totalRow = { background: '#F6F8F3' };
const wrap = { overflowX: 'auto' };
const tbl = { width: '100%', borderCollapse: 'collapse' };

export default function FinanceLens({ data, seesCosts, go, canSeeInvoices }) {
  const t = data.tracking;
  const T = t.totals;
  const I = t.invoicing;
  const S = t.asSold; // cost area only (the server strips it)
  const Q = t.quoted;
  const B = data.budget && data.budget.totals;
  const fee = feeText(T);
  const budget = budgetSoldOf(data);
  const nonBillable = (data.expenses || []).filter((e) => !e.billable).reduce((a, e) => a + (Number(e.amount) || 0), 0);
  const costs = !!(seesCosts && S);
  const tlFixed = T.tl + T.fixed;
  const anyWork = T.hours > 0 || T.expensesRaw > 0 || t.materialsSold > 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <Stat
          big
          label="As sold"
          value={money(T.soldTotal)}
          tone="#F1F8E9"
          accent={GREEN}
          note="What the work done so far is worth to the customer."
          tip={`Labour sold + expenses + the ${fee} handling fee + equipment & materials sold. The tracking sheet's AS SOLD (MASTER SUMMARY S8).`}
          from="Log time & expenses (hours x sell rates, expense days, receipts) and Materials"
        />
        {costs && (
          <Stat
            big
            label="As sold cost"
            value={money(S.cost)}
            note="What that work cost GLOBAL."
            tip={`Labour at each person's cost rate + expenses + the ${fee} fee + equipment & materials cost + non-billable receipts. MASTER SUMMARY S9.`}
            from="Log time & expenses x the cost rates in Setup > People & rates, and Materials"
          />
        )}
        {costs && (
          <Stat
            big
            label="Gross profit"
            value={money(S.profit)}
            accent={S.profit < 0 ? '#B71C1C' : undefined}
            note="As sold minus As sold cost."
            tip="AS SOLD - AS SOLD COST (MASTER SUMMARY S11)."
            from="the two figures to the left"
          />
        )}
        {costs && (
          <Stat
            big
            label="Margin"
            value={pct(S.margin)}
            accent={tone(S.margin)}
            note={S.margin > 0 ? `Of every $1 sold, ${Math.round(S.margin * 100)}¢ is profit.` : 'No profit on the work so far.'}
            tip="Gross profit / As sold (MASTER SUMMARY S10). Green from 30%, amber from 15%."
            from="As sold and As sold cost"
          />
        )}
        <Stat
          big
          label={budget.from === 'quoted' ? 'Quoted' : 'Budget sold'}
          value={budget.value ? money(budget.value) : '-'}
          note={budget.value ? `${pct(T.soldTotal / budget.value)} of it is As sold so far.` : 'No budget yet.'}
          tip={
            budget.from === 'quoted'
              ? "The Quoted figure (MASTER SUMMARY T8): the quoted hours at the first engineer's rates plus the quoted expenses."
              : "What the job was sold for: the won quote's worksheet plus the change orders (Setup > Budget)."
          }
          from="Setup > Budget"
        />
        {costs && (
          <Stat
            big
            label="Quoted margin"
            value={pct(Q.margin)}
            accent={tone(Q.margin)}
            note="If the budget is billed in full at today's cost."
            tip="(Quoted - labour cost - expenses with the fee) / Quoted (MASTER SUMMARY T10)."
            from="Setup > Budget and the cost so far"
          />
        )}
      </div>
      {!costs && (
        <div style={{ fontSize: 11.5, color: MUTED, background: '#F6F8F3', borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
          Cost, profit and margin are shown only to people with the <b>Staff cost rates &amp; COGS</b> access. Everything else on this page
          is the same for everyone.
        </div>
      )}

      <Section title="Money in" hint="The PO against what is invoiced and paid - from the Finance Invoice ledger; nothing is typed here.">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Stat
            label="PO value"
            value={money(I.poValue)}
            tip="The sum of the PO lines."
            from="Setup > PO lines (or the project's PO value)"
          />
          <Stat
            label="Invoiced"
            value={money(I.invoiced)}
            note={I.poValue ? `${pct(I.invoiced / I.poValue)} of the PO` : undefined}
            tip="Every live invoice of this project in the invoice ledger (MASTER SUMMARY N, INVOICE DETAILS)."
            from="Finance Invoice tab / Add an invoice on the Summary tab"
          />
          <Stat label="Paid" value={money(I.paid)} tip="Payments recorded against those invoices." from="Finance Invoice tab (payments)" />
          <Stat
            label="Open receivables"
            value={money(I.receivable)}
            accent={I.receivable > 0 ? '#B07503' : undefined}
            note="Invoiced and not paid yet."
            tip="Invoiced - paid."
            from="Finance Invoice tab"
          />
          <Stat
            label="PO remaining"
            value={money(I.open)}
            accent={I.open < 0 ? '#B71C1C' : undefined}
            note="What can still be billed on the PO."
            tip="PO value - invoiced (MASTER SUMMARY O)."
            from="Setup > PO lines and the invoice ledger"
          />
          <Stat
            label="Not invoiced yet"
            value={money(I.remainingOnSold)}
            accent={I.remainingOnSold < 0 ? '#B71C1C' : undefined}
            note={I.remainingOnSold < 0 ? 'More is invoiced than the work done so far.' : 'Work done that has not been invoiced.'}
            tip="As sold - invoiced (MASTER SUMMARY P3)."
            from="As sold and the invoice ledger"
          />
        </div>
        {canSeeInvoices && (
          <div style={{ marginTop: 10 }}>
            <a href="#/finance-invoices" style={{ fontSize: 11.5, fontWeight: 700, color: GREEN }}>
              Open the Finance Invoice tab
            </a>
          </div>
        )}
      </Section>

      <Section
        title={
          <>
            PO lines{' '}
            <Hint
              title="PO lines"
              tip="Each PO line's value, what is invoiced against it, what remains (Over / Under Budget), and the hours and sold booked to it on the day rows and materials."
              from="Setup > PO lines (values, which invoice goes to which line); Log time & expenses (the PO line of each day)"
            />
          </>
        }
        right={
          <button type="button" onClick={() => go('setup:po')} style={linkBtn}>
            Edit in Setup &rarr;
          </button>
        }
      >
        {I.lines.length === 0 ? (
          <Empty text="No PO lines yet." action="Add the PO lines" onAction={() => go('setup:po')} />
        ) : (
          <div style={wrap}>
            <table aria-label="PO lines summary" style={tbl}>
              <Head
                cols={[
                  'PO line',
                  'PO value',
                  'Invoiced',
                  'Remaining',
                  '% invoiced',
                  'Status',
                  'Hours booked',
                  'Sold booked',
                  ...(costs ? ['Cost booked'] : []),
                ]}
              />
              <tbody>
                {I.lines.map((l) => (
                  <tr key={l.label}>
                    <td style={label()}>
                      {l.label}
                      {!l.onPo && (
                        <span style={{ color: '#B07503' }} title="Used on days or materials but not a PO line">
                          {' '}
                          (not on the PO)
                        </span>
                      )}
                    </td>
                    <Num v={money(l.value)} />
                    <Num v={money(l.invoiced)} />
                    <Num v={money(l.remaining)} color={l.remaining < 0 ? '#B71C1C' : undefined} />
                    <td style={{ ...td, width: 90 }}>
                      <Bar share={l.value ? l.invoiced / l.value : 0} label={`${l.label} invoiced share`} />
                    </td>
                    <td
                      style={{
                        ...td,
                        fontSize: 10.5,
                        fontWeight: 700,
                        textAlign: 'right',
                        color: l.status === 'Over Budget' ? '#B71C1C' : GREEN,
                      }}
                    >
                      {l.status}
                    </td>
                    <Num v={hours(l.hours)} />
                    <Num v={money(l.sell)} color={l.value && l.sell > l.value ? '#B71C1C' : undefined} />
                    {costs && <Num v={money(l.cost)} />}
                  </tr>
                ))}
                <tr style={totalRow}>
                  <td style={{ ...td, fontSize: 12, fontWeight: 800 }}>Total</td>
                  <Num v={money(I.poValue)} strong />
                  <Num v={money(I.invoiced)} strong />
                  <Num v={money(I.open)} strong />
                  <td colSpan={costs ? 5 : 4} style={{ ...td, fontSize: 10.5, color: MUTED, textAlign: 'right' }}>
                    {I.noLine.hours ? `${hours(I.noLine.hours)} hrs are not booked to a PO line.` : ''}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title={
          <>
            As sold, built up{' '}
            <Hint
              title="As sold, built up"
              tip={`How the tracking sheet adds up AS SOLD${costs ? ' and AS SOLD COST' : ''}. The ${fee} handling fee is on the expenses${costs ? ', on both sides' : ''}.`}
              from="Log time & expenses, Materials, receipts; the budget from Setup > Budget"
            />
          </>
        }
        hint={
          data.trackingSheet
            ? `Imported from the tracking sheet ${data.trackingSheet.file.split('/').pop()} (Rev ${data.trackingSheet.revision}).`
            : undefined
        }
      >
        {!anyWork ? (
          <Empty
            text="Nothing is sold yet - no hours, expenses or materials are logged."
            action="Log time & expenses"
            onAction={() => go('log')}
          />
        ) : (
          <div style={wrap}>
            <table aria-label="As sold" style={tbl}>
              <Head cols={['', 'Sold', ...(costs ? ['Cost', 'Profit'] : [])]} />
              <tbody>
                <tr>
                  <td style={label()}>
                    Labour{' '}
                    <span style={{ color: MUTED, fontWeight: 500 }}>
                      ({hours(T.hours)} hrs, {money(T.effSell)}/hr effective)
                    </span>
                  </td>
                  <Num v={money(T.laborSell)} />
                  {costs && (
                    <>
                      <Num v={money(T.laborCost)} />
                      <Num v={money(T.laborSell - T.laborCost)} />
                    </>
                  )}
                </tr>
                <tr>
                  <td style={label()}>
                    Expenses{' '}
                    <span style={{ color: MUTED, fontWeight: 500 }}>
                      ({money(T.expensesRaw)} + {fee} fee)
                    </span>
                  </td>
                  <Num v={money(T.expensesWithFee)} />
                  {costs && (
                    <>
                      <Num v={money(T.expensesWithFee)} />
                      <Num v={money(0)} />
                    </>
                  )}
                </tr>
                <tr>
                  <td style={label()}>Equipment &amp; materials</td>
                  <Num v={money(t.materialsSold)} />
                  {costs && (
                    <>
                      <Num v={money(t.materialsCost)} />
                      <Num v={money(t.materialsSold - t.materialsCost)} />
                    </>
                  )}
                </tr>
                {costs && T.nonBillableExtra > 0 && (
                  <tr>
                    <td style={label()}>Non-billable receipts</td>
                    <Num v="-" />
                    <Num v={money(T.nonBillableExtra)} />
                    <Num v={money(-T.nonBillableExtra)} />
                  </tr>
                )}
                <tr style={totalRow}>
                  <td style={{ ...td, fontSize: 12, fontWeight: 800 }}>Total</td>
                  <Num v={money(T.soldTotal)} strong />
                  {costs && (
                    <>
                      <Num v={money(S.cost)} strong />
                      <Num v={money(S.profit)} strong />
                    </>
                  )}
                </tr>
                {B && (
                  <tr>
                    <td style={{ ...label(), color: MUTED }}>Budget</td>
                    <Num v={money(B.sold)} color={MUTED} />
                    {costs && (
                      <>
                        <Num v={money(B.cost)} color={MUTED} />
                        <Num v={money(B.profit)} color={MUTED} />
                      </>
                    )}
                  </tr>
                )}
                {B && (
                  <tr>
                    <td style={{ ...label(), color: MUTED }}>Budget left</td>
                    <Num v={money(B.sold - T.soldTotal)} color={B.sold - T.soldTotal < 0 ? '#B71C1C' : MUTED} />
                    {costs && (
                      <>
                        <Num v={money(B.cost - S.cost)} color={B.cost - S.cost < 0 ? '#B71C1C' : MUTED} />
                        <Num v="" />
                      </>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
        <Section
          title={
            <>
              T&amp;L / Fixed{' '}
              <Hint
                title="T&L / Fixed"
                tip="Sold + expenses (before the fee) split by each day's PO description: T&L is billed by the hour, Fixed is part of a fixed price (MASTER SUMMARY S2 / S3)."
                from="Log time & expenses (the T&L / Fixed of each day)"
              />
            </>
          }
        >
          {tlFixed <= 0 ? (
            <Empty text="No days logged yet." action="Log time & expenses" onAction={() => go('log')} />
          ) : (
            <>
              <div
                style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: '#EEF1EA', marginBottom: 8 }}
                role="img"
                aria-label="T&L and Fixed split"
              >
                <div style={{ width: `${(T.tl / tlFixed) * 100}%`, background: GREEN }} />
                <div style={{ width: `${(T.fixed / tlFixed) * 100}%`, background: '#8BC34A' }} />
              </div>
              <table aria-label="T&L and Fixed" style={tbl}>
                <tbody>
                  <tr>
                    <td style={label()}>
                      <Dot c={GREEN} />
                      T&amp;L
                    </td>
                    <Num v={money(T.tl)} />
                    <Num v={pct(T.tl / tlFixed)} />
                  </tr>
                  <tr>
                    <td style={label()}>
                      <Dot c="#8BC34A" />
                      Fixed
                    </td>
                    <Num v={money(T.fixed)} />
                    <Num v={pct(T.fixed / tlFixed)} />
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </Section>

        <Section
          title={
            <>
              Expenses{' '}
              <Hint
                title="Expenses"
                tip={`Billable expenses are the expense days (each person's per-day amounts) plus billable receipts; the customer pays them plus the ${fee} handling fee. Non-billable receipts are GLOBAL's own cost.`}
                from="Log time & expenses (expense days, receipts) and Setup > People & rates (per-day amounts)"
              />
            </>
          }
        >
          {T.expensesRaw <= 0 && nonBillable <= 0 ? (
            <Empty
              text="No expenses yet - tick Expense day on a logged day, or add a receipt."
              action="Log time & expenses"
              onAction={() => go('log')}
            />
          ) : (
            <table aria-label="Expenses billable and non-billable" style={tbl}>
              <tbody>
                {EXPENSE_COLS.filter((k) => T.expensesByCat[k]).map((k) => (
                  <tr key={k}>
                    <td style={label()}>{EXPENSE_LABELS[k]}</td>
                    <Num v={money(T.expensesByCat[k])} />
                  </tr>
                ))}
                <tr style={totalRow}>
                  <td style={label()}>Billable expenses</td>
                  <Num v={money(T.expensesRaw)} strong />
                </tr>
                <tr>
                  <td style={label()}>+ {fee} handling fee</td>
                  <Num v={money(T.expenseFee)} />
                </tr>
                <tr style={totalRow}>
                  <td style={{ ...td, fontSize: 12, fontWeight: 800 }}>Billed to the customer</td>
                  <Num v={money(T.expensesWithFee)} strong />
                </tr>
                <tr>
                  <td style={{ ...label(), color: MUTED }}>Non-billable receipts (GLOBAL's cost)</td>
                  <Num v={money(nonBillable)} color={MUTED} />
                </tr>
              </tbody>
            </table>
          )}
        </Section>
      </div>

      {costs && (
        <Section
          title={
            <>
              Project margin analysis{' '}
              <Hint
                title="Project margin analysis"
                tip="The price that would give a margin 1-5 points lower or higher, at today's As sold cost (MASTER SUMMARY B11-G17)."
                from="As sold cost"
              />
            </>
          }
        >
          {S.cost <= 0 ? (
            <Empty text="No cost yet - log time first." action="Log time & expenses" onAction={() => go('log')} />
          ) : (
            <div style={wrap}>
              <table aria-label="Margin analysis" style={tbl}>
                <Head cols={['Points', 'Lower margin', 'Price', 'Higher margin', 'Price']} />
                <tbody>
                  {t.marginAnalysis.map((m) => (
                    <tr key={m.step}>
                      <td style={label()}>{m.step}%</td>
                      <Num v={pct(m.lowerMargin)} />
                      <Num v={m.lowerPrice === null ? '-' : money(m.lowerPrice)} />
                      <Num v={pct(m.higherMargin)} />
                      <Num v={m.higherPrice === null ? '-' : money(m.higherPrice)} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      <Section
        title={
          <>
            Summary by task{' '}
            <Hint
              title="Summary by task"
              tip="Hours, labour sold and expenses by the tracking sheet's tasks (MASTER SUMMARY rows 20-32). A task off the list counts nowhere - a warning says so."
              from="Log time & expenses (the task of each day)"
            />
          </>
        }
      >
        {!T.hours && !T.expensesRaw ? (
          <Empty text="No hours logged yet." action="Log time & expenses" onAction={() => go('log')} />
        ) : (
          <div style={wrap}>
            <table aria-label="Finance by task" style={tbl}>
              <Head
                cols={[
                  'Task',
                  'NT',
                  'OT',
                  'DT',
                  'Labour sold',
                  ...EXPENSE_COLS.map((k) => EXPENSE_LABELS[k]),
                  'Expenses',
                  'Total sold',
                  ...(costs ? ['Cost', 'Margin'] : []),
                ]}
              />
              <tbody>
                {t.tasks
                  .filter((x) => x.hours || x.expenseTotal)
                  .map((x) => (
                    <tr key={x.task}>
                      <td style={label()}>{x.task}</td>
                      <Num v={hours(x.nt)} />
                      <Num v={hours(x.ot)} />
                      <Num v={hours(x.dt)} />
                      <Num v={money(x.sell)} />
                      {EXPENSE_COLS.map((k) => (
                        <Num key={k} v={money(x.expenses[k])} />
                      ))}
                      <Num v={money(x.expenseTotal)} />
                      <Num v={money(x.total)} strong />
                      {costs && (
                        <>
                          <Num v={money(x.cost)} />
                          <Num v={pct(x.margin)} color={x.sell ? tone(x.margin) : undefined} />
                        </>
                      )}
                    </tr>
                  ))}
                <tr style={totalRow}>
                  <td style={{ ...td, fontSize: 12, fontWeight: 800 }}>Totals</td>
                  <Num v={hours(T.nt)} strong />
                  <Num v={hours(T.ot)} strong />
                  <Num v={hours(T.dt)} strong />
                  <Num v={money(T.laborSell)} strong />
                  {EXPENSE_COLS.map((k) => (
                    <Num key={k} v={money(T.expensesByCat[k])} strong />
                  ))}
                  <Num v={money(T.expensesRaw)} strong />
                  <Num v={money(T.laborSell + T.expensesRaw)} strong />
                  {costs && (
                    <>
                      <Num v={money(T.laborCost)} strong />
                      <Num v={pct(T.laborSell ? (T.laborSell - T.laborCost) / T.laborSell : 0)} strong />
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title={
          <>
            By engineer{' '}
            <Hint
              title="By engineer"
              tip="Each person's rates, hours, what they sold and - for the cost area - what they cost (MASTER SUMMARY rows 37-60). Effective rate = sold / hours (OT and DT raise it). Weekly burn = average sold per working day x 7 + average expenses per expense day x 7."
              from="Setup > People & rates and Log time & expenses"
            />
          </>
        }
      >
        {!t.people.length ? (
          <Empty text="Nobody on this job yet." action="Add people in Setup" onAction={() => go('setup:people')} />
        ) : (
          <div style={wrap}>
            <table aria-label="Finance by engineer" style={tbl}>
              <Head
                cols={[
                  'Engineer',
                  'Sell rate',
                  'OT?',
                  'Eff. sell',
                  ...(costs ? ['Cost rate', 'OT?', 'Eff. cost'] : []),
                  'Hours',
                  'Sold',
                  'Expenses',
                  ...(costs ? ['Labour cost', 'Profit', 'Margin'] : []),
                  'Weekly burn',
                ]}
              />
              <tbody>
                {t.people.map((p) => (
                  <tr key={p.name}>
                    <td style={label()}>{p.name}</td>
                    <Num v={money(p.sellRate)} color={p.sellRate ? undefined : '#B71C1C'} />
                    <Num v={p.sellOt ? 'YES' : 'NO'} />
                    <Num v={money(p.effSell)} />
                    {costs && (
                      <>
                        <Num v={money(p.costRate)} color={p.costRate ? undefined : '#B71C1C'} />
                        <Num v={p.costOt ? 'YES' : 'NO'} />
                        <Num v={money(p.effCost)} />
                      </>
                    )}
                    <Num v={hours(p.hours)} />
                    <Num v={money(p.sell)} />
                    <Num v={money(p.expenseTotal)} />
                    {costs && (
                      <>
                        <Num v={money(p.laborCost)} />
                        <Num v={money(p.profit)} />
                        <Num v={pct(p.margin)} color={p.sell ? tone(p.margin) : undefined} />
                      </>
                    )}
                    <Num v={money(p.burn.weekly)} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

const Dot = ({ c }) => <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: c, marginRight: 6 }} />;
export const linkBtn = {
  border: 0,
  background: 'none',
  padding: 0,
  color: GREEN,
  fontWeight: 800,
  fontSize: 11.5,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
